/**
 * MelSearchPage
 * Page for searching MEL items with AI-powered smart search
 */

import React, { useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useToast } from '@/contexts/ToastContext';
import { useActiveAircraft } from '@/hooks/useAircraft';
import { useMelSmartSearch, useIssueMel, useActiveMelsByAircraft } from '@/hooks/useMel';
import {
  MelSearchInput,
  MelSearchResults,
  IssueMelDialog,
  MelExpiryWarning,
  ActiveMelCard,
} from '@/components/mel';
import type { MelItem, SmartSearchRequest } from '@/types/mel';
import {
  DocumentMagnifyingGlassIcon,
  PaperAirplaneIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';

export const MelSearchPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { showWarning, showSuccess, showError } = useToast();

  // Form state
  const [query, setQuery] = useState('');
  const [selectedAircraftType, setSelectedAircraftType] = useState('');
  const [selectedAircraftId, setSelectedAircraftId] = useState<number | undefined>(
    searchParams.get('aircraftId') ? Number(searchParams.get('aircraftId')) : undefined
  );

  // Search state
  const [searchRequest, setSearchRequest] = useState<SmartSearchRequest | null>(null);

  // Dialog state
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedMelItem, setSelectedMelItem] = useState<MelItem | null>(null);

  // Fetch aircraft
  const { data: aircraft = [], isLoading: isLoadingAircraft } = useActiveAircraft();

  // Fetch active MELs for selected aircraft (for context)
  const { data: activeMelsForAircraft = [] } = useActiveMelsByAircraft(
    selectedAircraftId || 0,
    !!selectedAircraftId
  );

  // Smart search query - auto-fetches when searchRequest is set
  const {
    data: searchResults,
    isLoading: isSearching,
    error: searchError,
  } = useMelSmartSearch(searchRequest, { enabled: !!searchRequest });

  // Issue MEL mutation
  const issueMelMutation = useIssueMel();

  // Get unique aircraft types
  const aircraftTypes = [...new Set(aircraft.map((a) => a.aircraftType))].sort();

  // Handle aircraft type selection
  const handleAircraftTypeChange = (type: string) => {
    setSelectedAircraftType(type);
    // Auto-select first aircraft of this type if only one
    const matchingAircraft = aircraft.filter((a) => a.aircraftType === type);
    if (matchingAircraft.length === 1) {
      setSelectedAircraftId(matchingAircraft[0].id);
    } else {
      setSelectedAircraftId(undefined);
    }
  };

  // Handle search - setting searchRequest triggers React Query auto-fetch
  const handleSearch = useCallback(() => {
    if (!query.trim() || !selectedAircraftType) {
      showWarning('Please enter a search query and select an aircraft type');
      return;
    }

    // Create a new request object each time to ensure React Query sees a new query key
    setSearchRequest({
      query: query.trim(),
      aircraftType: selectedAircraftType,
      limit: 10,
    });
  }, [query, selectedAircraftType, showWarning]);

  // Handle issuing MEL
  const handleIssueClick = useCallback((item: MelItem) => {
    setSelectedMelItem(item);
    setIsDialogOpen(true);
  }, []);

  const handleIssueMelConfirm = useCallback(
    async (aircraftId: number, melItemId: number, description: string) => {
      try {
        await issueMelMutation.mutateAsync({
          aircraftId,
          melItemId,
          description,
        });
        showSuccess('MEL issued successfully');
        setIsDialogOpen(false);
        setSelectedMelItem(null);
      } catch (error) {
        showError(
          error instanceof Error ? error.message : 'Failed to issue MEL'
        );
      }
    },
    [issueMelMutation, showSuccess, showError]
  );

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-lg bg-primary/10">
              <DocumentMagnifyingGlassIcon className="w-6 h-6 text-primary" />
            </div>
            <h1 className="text-3xl font-bold text-foreground">MEL Lookup</h1>
          </div>
          <p className="text-foreground/60">
            Describe the equipment issue and we'll find the relevant MEL entry for you to review
          </p>
        </div>

        {/* Active MELs Warning for Selected Aircraft */}
        {selectedAircraftId && activeMelsForAircraft.length > 0 && (
          <div className="mb-6">
            <MelExpiryWarning mels={activeMelsForAircraft} variant="compact" />
          </div>
        )}

        {/* Search Section */}
        <div className="bg-card rounded-xl border border-border p-6 mb-8">
          {/* Aircraft Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label
                htmlFor="aircraft-type"
                className="block text-sm font-medium text-foreground mb-1.5"
              >
                Aircraft Type <span className="text-danger">*</span>
              </label>
              <select
                id="aircraft-type"
                value={selectedAircraftType}
                onChange={(e) => handleAircraftTypeChange(e.target.value)}
                className="w-full h-11 px-3 rounded-md border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                disabled={isLoadingAircraft}
              >
                <option value="">Select aircraft type...</option>
                {aircraftTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                htmlFor="aircraft-tail"
                className="block text-sm font-medium text-foreground mb-1.5"
              >
                Specific Aircraft (Optional)
              </label>
              <select
                id="aircraft-tail"
                value={selectedAircraftId || ''}
                onChange={(e) =>
                  setSelectedAircraftId(e.target.value ? Number(e.target.value) : undefined)
                }
                className="w-full h-11 px-3 rounded-md border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                disabled={!selectedAircraftType || isLoadingAircraft}
              >
                <option value="">Any aircraft of this type</option>
                {aircraft
                  .filter((a) => a.aircraftType === selectedAircraftType)
                  .map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.tailNumber}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          {/* Search Input */}
          <MelSearchInput
            value={query}
            onChange={setQuery}
            onSearch={handleSearch}
            isLoading={isSearching}
            disabled={!selectedAircraftType}
          />
        </div>

        {/* Error State */}
        {searchError && (
          <div className="mb-8 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
            <div className="flex items-start gap-3">
              <ExclamationCircleIcon className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-red-800 dark:text-red-300">Search Error</h3>
                <p className="text-sm text-red-700 dark:text-red-400">
                  {searchError instanceof Error ? searchError.message : 'An error occurred during search'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Search Results */}
        {searchResults && (
          <MelSearchResults
            results={searchResults}
            onSelectItem={(item) => setSelectedMelItem(item)}
            selectedItemId={selectedMelItem?.id}
            onIssueClick={handleIssueClick}
          />
        )}

        {/* Empty State */}
        {!searchResults && !isSearching && !searchError && (
          <div className="text-center py-12">
            <PaperAirplaneIcon className="w-12 h-12 mx-auto text-foreground/20 mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">
              Look Up a MEL Entry
            </h3>
            <p className="text-foreground/60 max-w-md mx-auto">
              Select an aircraft type and describe the equipment issue in plain language.
              We'll search the MEL document and show you the most relevant entries to review.
            </p>
          </div>
        )}

        {/* Active MELs for Selected Aircraft */}
        {selectedAircraftId && activeMelsForAircraft.length > 0 && (
          <div className="mt-12">
            <h2 className="text-xl font-semibold text-foreground mb-4">
              Current Active MELs for {aircraft.find((a) => a.id === selectedAircraftId)?.tailNumber}
            </h2>
            <div className="space-y-4">
              {activeMelsForAircraft.map((mel) => (
                <ActiveMelCard key={mel.id} mel={mel} showAircraft={false} variant="compact" />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Issue MEL Dialog */}
      <IssueMelDialog
        isOpen={isDialogOpen}
        onClose={() => {
          setIsDialogOpen(false);
          setSelectedMelItem(null);
        }}
        onConfirm={handleIssueMelConfirm}
        melItem={selectedMelItem}
        aircraft={aircraft}
        selectedAircraftId={selectedAircraftId}
        isLoading={issueMelMutation.isPending}
        canDispatch={searchResults?.aiExplanation?.canDispatch}
      />
    </div>
  );
};

export default MelSearchPage;
